import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useToast } from '../components/common/ToastProvider';
import {
    getCurrentUser,
    getFriendlyErrorMessage,
    signOutUser,
    isAuthenticated,
    listOrders,
    updateUserPassword,
} from '../lib/artTasteApi';
import { formatDate, formatCurrency, getStatusMeta } from '../lib/format';

export default function UserDashboard() {
    const navigate = useNavigate();
    const { showToast } = useToast();
    const [currentUser, setCurrentUser] = useState(null);
    const [activeTab, setActiveTab] = useState('orders');
    const [userOrders, setUserOrders] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [profileForm, setProfileForm] = useState({
        name: '',
        email: '',
    });
    const [passwordForm, setPasswordForm] = useState({
        currentPassword: '',
        newPassword: '',
        confirmPassword: '',
    });
    const [isUpdatingProfile, setIsUpdatingProfile] = useState(false);
    const [isUpdatingPassword, setIsUpdatingPassword] = useState(false);

    // Redirect if not authenticated
    useEffect(() => {
        if (!isAuthenticated()) {
            navigate('/auth');
            return;
        }

        const user = getCurrentUser();
        setCurrentUser(user);
        setProfileForm({
            name: user?.name || '',
            email: user?.email || '',
        });

        loadUserOrders();
    }, [navigate]);

    const loadUserOrders = async () => {
        setIsLoading(true);
        try {
            const orders = await listOrders();
            setUserOrders(orders);
        } catch (error) {
            console.error('Failed to load orders:', error);
            showToast(
                getFriendlyErrorMessage(error, 'We could not load your orders right now.'),
                'error',
            );
        } finally {
            setIsLoading(false);
        }
    };

    const handleProfileChange = (field) => (event) => {
        setProfileForm((prev) => ({
            ...prev,
            [field]: event.target.value,
        }));
    };

    const handleUpdateProfile = async (event) => {
        event.preventDefault();
        setIsUpdatingProfile(true);

        try {
            // TODO: Implement profile update API endpoint
            showToast('Profile update functionality coming soon!', 'info');
            setIsUpdatingProfile(false);
        } catch (error) {
            showToast(
                getFriendlyErrorMessage(error, 'We could not update your profile right now.'),
                'error',
            );
            setIsUpdatingProfile(false);
        }
    };

    const handlePasswordChange = (field) => (event) => {
        setPasswordForm((prev) => ({
            ...prev,
            [field]: event.target.value,
        }));
    };

    const handleUpdatePassword = async (event) => {
        event.preventDefault();

        if (!passwordForm.currentPassword) {
            showToast('Please enter your current password', 'error');
            return;
        }

        if (passwordForm.newPassword !== passwordForm.confirmPassword) {
            showToast('New passwords do not match', 'error');
            return;
        }

        if (passwordForm.newPassword.length < 6) {
            showToast('Password must be at least 6 characters', 'error');
            return;
        }

        setIsUpdatingPassword(true);

        try {
            await updateUserPassword(passwordForm.currentPassword, passwordForm.newPassword);
            showToast('Password updated successfully', 'success');
            setPasswordForm({
                currentPassword: '',
                newPassword: '',
                confirmPassword: '',
            });
            setIsUpdatingPassword(false);
        } catch (error) {
            showToast(
                getFriendlyErrorMessage(error, 'We could not change your password right now.'),
                'error',
            );
            setIsUpdatingPassword(false);
        }
    };

    const handleSignOut = async () => {
        signOutUser();
        showToast('Signed out successfully', 'info');
        navigate('/');
    };

    if (!currentUser) {
        return <div className="dashboard-loading">Loading...</div>;
    }

    return (
        <div className="user-dashboard">
            <div className="dashboard-header">
                <div className="dashboard-title">
                    <h1>My Dashboard</h1>
                    <p>Welcome, {currentUser.name}!</p>
                </div>
                <button onClick={handleSignOut} className="btn-secondary">
                    Sign Out
                </button>
            </div>

            <div className="dashboard-container">
                <div className="dashboard-tabs">
                    <button
                        className={`tab-button ${activeTab === 'orders' ? 'active' : ''}`}
                        onClick={() => setActiveTab('orders')}
                    >
                        📦 Orders
                    </button>
                    <button
                        className={`tab-button ${activeTab === 'profile' ? 'active' : ''}`}
                        onClick={() => setActiveTab('profile')}
                    >
                        👤 Profile
                    </button>
                    <button
                        className={`tab-button ${activeTab === 'security' ? 'active' : ''}`}
                        onClick={() => setActiveTab('security')}
                    >
                        🔒 Security
                    </button>
                </div>

                <div className="dashboard-content">
                    {/* Orders Tab */}
                    {activeTab === 'orders' && (
                        <div className="tab-pane">
                            <h2>Your Orders</h2>
                            {isLoading ? (
                                <p>Loading orders...</p>
                            ) : userOrders.length === 0 ? (
                                <div className="empty-state">
                                    <p>No orders yet</p>
                                    <a href="/#order" className="btn-primary">
                                        Place Your First Order
                                    </a>
                                </div>
                            ) : (
                                <div className="orders-list">
                                    {userOrders.map((order) => {
                                        const statusMeta = getStatusMeta(order.status || 'paid');
                                        return (
                                            <div key={order.id || order.orderId} className="order-card">
                                                <div className="order-header">
                                                    <div>
                                                        <h3>Order #{order.orderId}</h3>
                                                        <p className="order-date">
                                                            {formatDate(order.createdAt)}
                                                        </p>
                                                    </div>
                                                    <span className={`status-badge tone-${statusMeta.tone}`}>
                                                        {statusMeta.label}
                                                    </span>
                                                </div>

                                                <div className="order-details">
                                                    <div className="detail-row">
                                                        <span>Customer:</span>
                                                        <strong>{order.customer?.name}</strong>
                                                    </div>
                                                    <div className="detail-row">
                                                        <span>Files:</span>
                                                        <strong>{order.files?.length || 0} file(s)</strong>
                                                    </div>
                                                    <div className="detail-row">
                                                        <span>Print Type:</span>
                                                        <strong>{order.printOptions?.type || 'B&W'}</strong>
                                                    </div>
                                                    <div className="detail-row">
                                                        <span>Delivery:</span>
                                                        <strong className="text-capitalize">
                                                            {order.printOptions?.delivery || 'Standard'}
                                                        </strong>
                                                    </div>
                                                </div>

                                                <div className="order-footer">
                                                    <div className="order-amount">
                                                        Total: <strong>{formatCurrency(order.totalAmount || 0)}</strong>
                                                    </div>
                                                    {order.files?.length > 0 && (
                                                        <details className="files-preview">
                                                            <summary>View Files ({order.files.length})</summary>
                                                            <ul>
                                                                {order.files.map((file, idx) => (
                                                                    <li key={idx}>{file.name}</li>
                                                                ))}
                                                            </ul>
                                                        </details>
                                                    )}
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                        </div>
                    )}

                    {/* Profile Tab */}
                    {activeTab === 'profile' && (
                        <div className="tab-pane">
                            <h2>Profile Information</h2>
                            <form className="profile-form" onSubmit={handleUpdateProfile}>
                                <div className="form-group">
                                    <label>Full Name</label>
                                    <input
                                        type="text"
                                        value={profileForm.name}
                                        onChange={handleProfileChange('name')}
                                        placeholder="Your full name"
                                        disabled={isUpdatingProfile}
                                    />
                                </div>

                                <div className="form-group">
                                    <label>Email Address</label>
                                    <input
                                        type="email"
                                        value={profileForm.email}
                                        onChange={handleProfileChange('email')}
                                        placeholder="your@email.com"
                                        disabled={isUpdatingProfile}
                                    />
                                </div>

                                <button
                                    type="submit"
                                    className="btn-primary"
                                    disabled={isUpdatingProfile}
                                >
                                    {isUpdatingProfile ? 'Updating...' : 'Update Profile'}
                                </button>
                            </form>
                        </div>
                    )}

                    {/* Security Tab */}
                    {activeTab === 'security' && (
                        <div className="tab-pane">
                            <h2>Change Password</h2>
                            <form className="password-form" onSubmit={handleUpdatePassword}>
                                <div className="form-group">
                                    <label>Current Password</label>
                                    <input
                                        type="password"
                                        value={passwordForm.currentPassword}
                                        onChange={handlePasswordChange('currentPassword')}
                                        placeholder="Enter current password"
                                        disabled={isUpdatingPassword}
                                    />
                                </div>

                                <div className="form-group">
                                    <label>New Password</label>
                                    <input
                                        type="password"
                                        value={passwordForm.newPassword}
                                        onChange={handlePasswordChange('newPassword')}
                                        placeholder="Enter new password"
                                        disabled={isUpdatingPassword}
                                    />
                                </div>

                                <div className="form-group">
                                    <label>Confirm New Password</label>
                                    <input
                                        type="password"
                                        value={passwordForm.confirmPassword}
                                        onChange={handlePasswordChange('confirmPassword')}
                                        placeholder="Confirm new password"
                                        disabled={isUpdatingPassword}
                                    />
                                </div>

                                <button
                                    type="submit"
                                    className="btn-primary"
                                    disabled={isUpdatingPassword}
                                >
                                    {isUpdatingPassword ? 'Updating...' : 'Change Password'}
                                </button>
                            </form>
                        </div>
                    )}
                </div>
            </div>

            <style>{`
        .user-dashboard {
          min-height: 100vh;
          background: linear-gradient(135deg, #f5f0e8 0%, #faf7f2 100%);
          padding: 2rem;
        }

        .dashboard-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 3rem;
          background: white;
          padding: 2rem;
          border-radius: 1rem;
          box-shadow: 0 4px 6px rgba(0, 0, 0, 0.05);
        }

        .dashboard-title h1 {
          margin: 0;
          font-size: 2rem;
          color: #1a1209;
        }

        .dashboard-title p {
          margin: 0.5rem 0 0 0;
          color: #7a6f60;
        }

        .dashboard-container {
          display: grid;
          grid-template-columns: 200px 1fr;
          gap: 2rem;
          max-width: 1200px;
          margin: 0 auto;
        }

        .dashboard-tabs {
          display: flex;
          flex-direction: column;
          gap: 0.5rem;
          height: fit-content;
          position: sticky;
          top: 2rem;
        }

        .tab-button {
          padding: 1rem;
          background: white;
          border: none;
          border-radius: 0.5rem;
          text-align: left;
          cursor: pointer;
          font-weight: 500;
          color: #7a6f60;
          transition: all 0.2s;
          box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
        }

        .tab-button:hover {
          background: #f9f7f2;
          color: #1a1209;
        }

        .tab-button.active {
          background: #1a1209;
          color: white;
        }

        .dashboard-content {
          background: white;
          padding: 2rem;
          border-radius: 1rem;
          box-shadow: 0 4px 6px rgba(0, 0, 0, 0.05);
        }

        .tab-pane h2 {
          margin-top: 0;
          color: #1a1209;
          margin-bottom: 1.5rem;
        }

        .empty-state {
          text-align: center;
          padding: 2rem;
          color: #7a6f60;
        }

        .empty-state p {
          font-size: 1.125rem;
          margin-bottom: 1rem;
        }

        .orders-list {
          display: grid;
          gap: 1.5rem;
        }

        .order-card {
          border: 1px solid rgba(26, 18, 9, 0.1);
          border-radius: 0.75rem;
          padding: 1.5rem;
          transition: box-shadow 0.2s;
        }

        .order-card:hover {
          box-shadow: 0 4px 12px rgba(26, 18, 9, 0.08);
        }

        .order-header {
          display: flex;
          justify-content: space-between;
          align-items: start;
          margin-bottom: 1rem;
        }

        .order-header h3 {
          margin: 0;
          color: #1a1209;
        }

        .order-date {
          margin: 0.25rem 0 0 0;
          font-size: 0.875rem;
          color: #7a6f60;
        }

        .status-badge {
          padding: 0.5rem 1rem;
          border-radius: 999px;
          font-size: 0.875rem;
          font-weight: 600;
        }

        .order-details {
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          gap: 1rem;
          margin-bottom: 1rem;
          padding-bottom: 1rem;
          border-bottom: 1px solid rgba(26, 18, 9, 0.1);
        }

        .detail-row {
          display: flex;
          justify-content: space-between;
          font-size: 0.875rem;
        }

        .detail-row span {
          color: #7a6f60;
        }

        .detail-row strong {
          color: #1a1209;
          text-transform: capitalize;
        }

        .order-footer {
          display: flex;
          justify-content: space-between;
          align-items: center;
        }

        .order-amount {
          font-size: 0.875rem;
          color: #7a6f60;
        }

        .order-amount strong {
          font-size: 1.125rem;
          color: #c04a1e;
        }

        .files-preview {
          cursor: pointer;
          color: #1a5276;
          font-size: 0.875rem;
        }

        .files-preview summary:hover {
          text-decoration: underline;
        }

        .files-preview ul {
          margin-top: 0.5rem;
          padding-left: 1.5rem;
          font-size: 0.8125rem;
          color: #7a6f60;
        }

        .profile-form,
        .password-form {
          display: grid;
          gap: 1.5rem;
          max-width: 500px;
        }

        .form-group {
          display: flex;
          flex-direction: column;
        }

        .form-group label {
          font-weight: 500;
          color: #1a1209;
          margin-bottom: 0.5rem;
        }

        .form-group input {
          padding: 0.75rem;
          border: 1px solid rgba(26, 18, 9, 0.2);
          border-radius: 0.5rem;
          font-size: 1rem;
          transition: border-color 0.2s;
        }

        .form-group input:focus {
          outline: none;
          border-color: #c04a1e;
          box-shadow: 0 0 0 3px rgba(192, 74, 30, 0.1);
        }

        .form-group input:disabled {
          background: #f5f0e8;
          cursor: not-allowed;
        }

        .dashboard-loading {
          display: flex;
          align-items: center;
          justify-content: center;
          min-height: 100vh;
          font-size: 1.125rem;
          color: #7a6f60;
        }

        @media (max-width: 768px) {
          .dashboard-container {
            grid-template-columns: 1fr;
          }

          .dashboard-tabs {
            display: grid;
            grid-template-columns: repeat(3, 1fr);
            gap: 1rem;
            position: static;
          }

          .order-details {
            grid-template-columns: 1fr;
          }

          .order-header {
            flex-direction: column;
            gap: 0.5rem;
          }
        }
      `}</style>
        </div>
    );
}
